import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
	IconButton,
	OutlinedPrimaryButton,
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalFooter,
	ModalTitle,
} from "src/components/Modals/Components/ModalsComponents";
import { humanFileSize } from "src/components/Uploader/ImageFileUploader";
import { getUserInfo, useEditUser, useUploadAvatar } from "src/hooks/useUser";
import CloseIcon from "src/images/Icons/newIcons/close.svg";
import PhotoCameraIcon from "src/images/Icons/newIcons/photo.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/account.svg";
import { ModalMaxWidth } from "src/types/enums";

interface SetAvatarModalProps {
	onClose: () => void;
}

interface AvatarValue {
	name: string;
	size: number;
	avatarUrl?: string;
}

const avatarsUrls = [
	"https://cdn.egzaminlek.pl/production/public/avatars/Body.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-1.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-2.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-3.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-4.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-5.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-6.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-7.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-8.png",
	"https://cdn.egzaminlek.pl/production/public/avatars/Body-9.png",
];

const getAvatarUrl = (avatarUrl: string): AvatarValue => {
	return {
		name: "Egzaminlek - avatar",
		size: 0,
		avatarUrl,
	};
};

export const SetAvatarModal = ({ onClose }: SetAvatarModalProps) => {
	const userInfo = getUserInfo();
	const { mutateAsync } = useEditUser() as unknown as {
		mutateAsync: (values: { avatarUrl: string }) => Promise<void>;
	};
	const { mutateAsync: uploadAvatar } = useUploadAvatar() as unknown as {
		mutateAsync: (values: { file: AvatarValue | File }) => Promise<string>;
	};
	const { t } = useTranslation();
	const { handleSubmit, setFieldValue, values } = useFormik({
		initialValues: {
			avatar: userInfo.avatarUrl
				? getAvatarUrl(userInfo.avatarUrl)
				: (null as AvatarValue | File | null),
		},
		onSubmit: async ({ avatar }, { resetForm }) => {
			let avatarUrl = (avatar as AvatarValue)?.avatarUrl;
			if (!avatarUrl && avatar) {
				avatarUrl = await uploadAvatar({ file: avatar });
			}
			await mutateAsync({
				avatarUrl: avatar ? (avatarUrl ?? "") : "",
			});
			resetForm();
			onClose();
		},
	});

	const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.currentTarget.files?.[0];
		if (!file) return;

		const allowedTypes = ["image/jpeg", "image/png"];
		if (!allowedTypes.includes(file.type)) {
			setFieldValue("avatar", null);
			return;
		}

		setFieldValue("avatar", file);
	};

	const renderUploadPhoto = () => {
		if (values.avatar) {
			return null;
		}

		return (
			<div className="flex-center-row gap-16">
				<OutlinedPrimaryButton
					component="label"
					startIcon={<PhotoCameraIcon className="icon-normal" />}
				>
					Dodaj zdjęcie
					<input
						hidden
						type="file"
						accept="image/jpeg,image/png"
						onChange={handleAvatarChange}
					/>
				</OutlinedPrimaryButton>
				<div className="flex flex-col">
					<span className="size-14-light text-grey">Formaty: jpg, png</span>
					<span className="size-14-light text-grey">Rozmiar max: 1 MB</span>
				</div>
			</div>
		);
	};

	const renderUploadedPhoto = () => {
		if (!values.avatar) {
			return null;
		}
		const avatarValue = values.avatar as AvatarValue & File;
		const src = avatarValue.avatarUrl || URL.createObjectURL(avatarValue);
		return (
			<div className="flex items-cente gap-16">
				<div className="flex gap-8 justify-between items-center w-100">
					<div className="flex gap-8 justify-center">
						<img
							src={src}
							alt="avatar"
							className="cursor-pointer avatar avatar-circle avatar-64"
						/>
						<div className="flex flex-col justify-center gap-4">
							<span className="size-20">{avatarValue.name}</span>
							<span className="size-18-light text-grey">
								{humanFileSize(avatarValue.size)}
							</span>
						</div>
					</div>
					<IconButton
						className="h-fit-content icon-button--error"
						onClick={() => setFieldValue("avatar", null)}
					>
						<CloseIcon className="icon-normal icon-error" />
					</IconButton>
				</div>
			</div>
		);
	};

	return (
		<Modal onClose={onClose} maxWidth={ModalMaxWidth.sm}>
			<div className="flex flex-col items-center gap-8">
				<ModalTitle title="Zdjęcie profilowe" Icon={ProfileIcon} />
				<span className="size-16-light text-center">
					Wybierz awatara z dostępnej bazy lub dodaj własne zdjęcie
				</span>
			</div>
			<div className="flex flex-col gap-32 w-100 mt-16">
				{renderUploadPhoto()}
				{renderUploadedPhoto()}
				<div className="flex flex-col">
					<span className="size-16">Wybierz z bazy</span>
					<div className="grid grid-cols-5 gap-8 py-12 justify-items-center grid-gap-20">
						{avatarsUrls.map((url) => (
							<img
								key={url}
								src={url}
								alt="avatar"
								className="cursor-pointer avatar avatar-circle avatar-64"
								onClick={() =>
									setFieldValue("avatar", {
										name: "Egzaminlek - avatar",
										size: 0,
										avatarUrl: url,
									})
								}
							/>
						))}
					</div>
				</div>
			</div>
			<ModalFooter>
				<TextPrimaryButton onClick={onClose}>
					{t("commands.cancel")}
				</TextPrimaryButton>
				<PrimaryButton onClick={() => handleSubmit()}>
					{t("commands.save")}
				</PrimaryButton>
			</ModalFooter>
		</Modal>
	);
};
